import React from 'react';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { Tool } from '../types';
import { History, Share2, Star, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';

interface ToolLayoutProps {
  tool: Tool;
  children: React.ReactNode;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onShare?: () => void;
  onHistory?: () => void;
}

export default function ToolLayout({ tool, children, isFavorite, onToggleFavorite, onShare, onHistory }: ToolLayoutProps) {
  const pageTitle = `${tool.name} - QuicklyUtils`;
  const pageDesc = tool.description || `Use our free ${tool.name} tool to speed up your workflow. Fast, secure, and easy to use.`;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="min-h-full flex flex-col p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto w-full transition-colors duration-300"
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://quicklyutils.com${tool.path}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDesc} />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "${tool.name}",
              "operatingSystem": "All",
              "applicationCategory": "UtilityApplication",
              "description": "${pageDesc}",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://quicklyutils.com/"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "${tool.name}",
                "item": "https://quicklyutils.com${tool.path}"
              }]
            }
          `}
        </script>
      </Helmet>

      <header className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div className="flex-1 overflow-hidden">
          <Link to="/" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600 hover:text-accent transition-colors mb-4">
            <ChevronLeft className="w-3 h-3" />
            Return to Dashboard
          </Link>
          <div className="flex items-center gap-4 md:gap-5">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-accent text-white rounded-2xl flex items-center justify-center shadow-lg shadow-accent/20 shrink-0">
              <tool.icon className="w-6 h-6 md:w-7 md:h-7" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-1 dark:text-white truncate">
                {tool.name}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 text-xs md:text-sm max-w-lg truncate md:whitespace-normal">
                {tool.description}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <button 
            aria-label="Favorite"
            onClick={onToggleFavorite}
            title="Favorite" 
            className={cn(
              "p-3.5 bg-white dark:bg-slate-900 border border-transparent dark:border-slate-800 rounded-2xl hover:border-accent hover:scale-105 active:scale-95 group transition-all duration-300 shadow-sm sm:shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-md dark:shadow-none",
              isFavorite ? "border-accent/50 bg-accent/[0.03]" : ""
            )}
          >
            <Star className={cn("w-5 h-5 transition-all duration-300", isFavorite ? "text-accent fill-accent" : "text-slate-400 dark:text-slate-500 group-hover:text-accent")} />
          </button>
          
          <button 
            aria-label="Share"
            onClick={onShare}
            title="Share" 
            className="p-3.5 bg-white dark:bg-slate-900 border border-transparent dark:border-slate-800 rounded-2xl hover:border-accent hover:scale-105 active:scale-95 group transition-all duration-300 shadow-sm sm:shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-md dark:shadow-none"
          >
            <Share2 className="w-5 h-5 text-slate-400 dark:text-slate-500 group-hover:text-accent transition-colors duration-300" />
          </button>
          
          <button 
            aria-label="History"
            onClick={onHistory}
            title="History" 
            className="p-3.5 bg-white dark:bg-slate-900 border border-transparent dark:border-slate-800 rounded-2xl hover:border-accent hover:scale-105 active:scale-95 group transition-all duration-300 shadow-sm sm:shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-md dark:shadow-none"
          >
            <History className="w-5 h-5 text-slate-400 dark:text-slate-500 group-hover:text-accent transition-colors duration-300" />
          </button>
        </div>
      </header>

      <div className="flex-1 w-full">
        {children}
      </div>

      <footer className="mt-8 flex justify-between items-center text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <div className="flex gap-6">
          <span>Developed by <a href="https://allysonbelo.com" target="_blank" rel="noreferrer" className="text-accent hover:underline">Allyson Belo</a></span>
          <span className="hidden sm:inline">Version 2.4.0</span>
          <span className="hidden sm:inline">Bento Engine v1</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
          All systems operational
        </div>
      </footer>
    </motion.div>
  );
}
